import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

/**
 * Pet Store quick add: buttons with [data-ps-add][data-variant-id] add one unit
 * to the cart and notify Horizon's cart (drawer + bubble) through the standard
 * cart lines event, the same way the theme's own product form does.
 */
const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

document.addEventListener('click', async (event) => {
  const button = event.target.closest('[data-ps-add]');
  if (!button) return;
  event.preventDefault();
  if (button.getAttribute('aria-busy') === 'true') return;

  const variantId = button.dataset.variantId;
  if (!variantId) return;

  button.setAttribute('aria-busy', 'true');

  const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
    .map((el) => el.dataset.sectionId)
    .filter(Boolean);

  const deferred = CartLinesUpdateEvent.createPromise();
  button.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: variantId, quantity: 1 }],
      promise: deferred.promise,
    })
  );

  try {
    const response = await fetch(root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }], sections: sectionIds.join(',') }),
    }).then((r) => r.json());

    const cart = await fetch(root + 'cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'ps-quick-add',
        sourceId: 'ps-quick-add',
        itemCount: 1,
        sections: response.sections,
        didError: Boolean(response.status),
      },
    });

    if (!response.status) {
      button.dataset.added = 'true';
      setTimeout(() => button.removeAttribute('data-added'), 1600);
    }
  } catch (error) {
    deferred.reject(error);
    console.error('[ps-quick-add]', error);
  } finally {
    button.removeAttribute('aria-busy');
  }
});

/**
 * Product page: "Save 21%" badge on the main gallery, driven by the savings block.
 */
function syncGalleryBadge(scope) {
  const base = scope || document;
  base.querySelectorAll('.product-information').forEach((info) => {
    const host = info.querySelector('media-gallery slideshow-container');
    if (!host) return;
    const pill = info.querySelector('[data-ps-savings] [data-off]');
    let badge = host.querySelector(':scope > .ps-gallery-badge');
    if (!pill) {
      if (badge) badge.remove();
      return;
    }
    if (!badge) {
      badge = document.createElement('span');
      badge.className = 'ps-gallery-badge';
      badge.setAttribute('aria-hidden', 'true');
      host.appendChild(badge);
    }
    badge.textContent = 'Save ' + pill.getAttribute('data-off') + '%';
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => syncGalleryBadge());
} else {
  syncGalleryBadge();
}
document.addEventListener('shopify:section:load', (e) => syncGalleryBadge(e.target));

/**
 * Keep Pet Store product blocks in sync when the shopper picks another variant:
 * Horizon hands over the freshly rendered section HTML.
 */
const SYNC_SELECTORS = ['[data-ps-savings]', '[data-ps-sticky-price]'];

document.addEventListener(
  StandardEvents.productSelect,
  (event) => {
    const scope = (event.target && event.target.closest && event.target.closest('.shopify-section')) || document;
    if (!event.promise) return;
    event.promise
      .then((result) => {
        const html = result && result.detail && result.detail.html;
        if (!html || !html.querySelector) return;
        SYNC_SELECTORS.forEach((selector) => {
          const current = scope.querySelector(selector);
          const next = html.querySelector(selector);
          if (current && next) current.innerHTML = next.innerHTML;
        });
        syncGalleryBadge(scope);
        setTimeout(() => syncGalleryBadge(scope), 300);
      })
      .catch(() => {});
  },
  true
);

/**
 * Floating header over the home banner: flag the page once it scrolls so the
 * sticky header card can round all four corners.
 */
function markScrolled() {
  const wrapper = document.querySelector('.page-wrapper');
  const y = Math.max(window.scrollY || 0, document.documentElement.scrollTop || 0, wrapper ? wrapper.scrollTop : 0);
  document.body.classList.toggle('ps-scrolled', y > 60);
}
document.addEventListener('scroll', markScrolled, { capture: true, passive: true });
markScrolled();
